import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { LayoutConfig } from '@/types/customizable';

export const usePreferencesStore = defineStore(
  'preferences',
  () => {
    const selectedRiskProfileId: Ref<string | null> = ref(localStorage.getItem('selectedRiskProfileId') || null);

    function setSelectedRiskProfileId(profileId: string | null): void {
      selectedRiskProfileId.value = profileId;
    }

    watch(selectedRiskProfileId, (newId) => {
      if (newId) {
        localStorage.setItem('selectedRiskProfileId', newId);
      } else {
        localStorage.removeItem('selectedRiskProfileId');
      }
    });

    // Last KYT risk policy the user looked at — restored across entity switches, global-view
    // round-trips, and page reloads (the KYT analog of selectedRiskProfileId).
    const selectedKytPolicyId: Ref<string | null> = ref(localStorage.getItem('selectedKytPolicyId') || null);

    function setSelectedKytPolicyId(policyId: string | null): void {
      selectedKytPolicyId.value = policyId;
    }

    watch(selectedKytPolicyId, (newId) => {
      if (newId) {
        localStorage.setItem('selectedKytPolicyId', newId);
      } else {
        localStorage.removeItem('selectedKytPolicyId');
      }
    });

    // ── Customizable layout blobs ────────────────────────────────────────────
    // A dumb keyed store for saved dashboard layouts. The key encodes user +
    // account + context (built by the customizable-layout store), so a user can
    // keep a different arrangement per company. This layer knows nothing about
    // what the blob means — only how to read and write it.
    function getLayout(key: string): LayoutConfig | null {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as LayoutConfig;
      } catch {
        return null;
      }
    }

    function setLayout(key: string, layout: LayoutConfig): void {
      localStorage.setItem(key, JSON.stringify(layout));
    }

    function clearLayout(key: string): void {
      localStorage.removeItem(key);
    }

    return {
      selectedRiskProfileId,
      setSelectedRiskProfileId,
      selectedKytPolicyId,
      setSelectedKytPolicyId,
      getLayout,
      setLayout,
      clearLayout,
    };
  },
);
